import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  applyDocumentSeo,
  buildOrganizationJsonLd,
  buildWebSiteJsonLd,
} from './documentSeo.js';
import { getPublicBrandConfig } from './publicBrandConfig.js';

function localeToOg(lang: string): string {
  if (lang.startsWith('sk')) {
    return 'sk_SK';
  }
  if (lang.startsWith('en')) {
    return 'en_US';
  }
  return 'cs_CZ';
}

function isPrivateCustomerPath(pathname: string): boolean {
  if (pathname.includes('/sign-in') || pathname.includes('/sign-up')) {
    return true;
  }
  if (pathname.includes('/account')) {
    return true;
  }
  if (pathname.includes('/forgot-password') || pathname.includes('/reset-password')) {
    return true;
  }
  if (pathname.includes('/confirm-email') || pathname.includes('/add-phone')) {
    return true;
  }
  return false;
}

/**
 * Route-aware document SEO for the customer public surface.
 * Origins and brand come from VITE_PUBLIC_* / live location — never hardcoded.
 */
export function CustomerDocumentSeo(): null {
  const { t, i18n } = useTranslation('customer');
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    const { brandName, siteOrigin, sisterSiteOrigin } = getPublicBrandConfig();
    const brand = brandName;
    const siteName = t('seo.siteName', { brand });
    const locale = localeToOg(i18n.language);

    if (isPrivateCustomerPath(path)) {
      applyDocumentSeo(
        {
          title: t('seo.privateTitle', { brand }),
          description: t('seo.privateDescription', { brand }),
          canonicalPath: path.length > 0 ? path : '/',
          siteName,
          robots: 'noindex,nofollow',
          locale,
          imagePath: '/icon.svg',
          jsonLd: null,
        },
        siteOrigin,
      );
      return;
    }

    const isBrowse = path === '/browse' || path.startsWith('/browse/');
    const isShops = path === '/shops' || path.startsWith('/shops/');
    const isOrgs = path === '/orgs' || path.startsWith('/orgs/');
    const isHome = path === '/' || path === '';

    let title = t('seo.defaultTitle', { brand });
    let description = t('seo.defaultDescription', { brand });
    let canonicalPath = isHome ? '/browse' : path;

    if (isBrowse || isHome) {
      title = t('seo.browse.title', { brand });
      description = t('seo.browse.description', { brand });
      canonicalPath = '/browse';
    } else if (isShops) {
      title = t('seo.shops.title', { brand });
      description = t('seo.shops.description', { brand });
      canonicalPath = '/shops';
    } else if (isOrgs) {
      title = t('seo.orgs.title', { brand });
      description = t('seo.orgs.description', { brand });
      canonicalPath = '/orgs';
    }

    const sameAs = [siteOrigin, sisterSiteOrigin].filter(
      (value): value is string => typeof value === 'string' && value.length > 0,
    );

    const jsonLd =
      (isBrowse || isHome) && siteOrigin.length > 0
        ? {
            '@context': 'https://schema.org',
            '@graph': [
              buildOrganizationJsonLd({
                name: brand,
                url: siteOrigin,
                description,
                sameAs,
              }),
              buildWebSiteJsonLd({
                name: siteName,
                url: siteOrigin,
                description,
                searchTargetPath: '/browse?q={search_term_string}',
              }),
            ],
          }
        : null;

    applyDocumentSeo(
      {
        title,
        description,
        canonicalPath,
        siteName,
        robots: 'index,follow',
        locale,
        alternateLocales: ['cs', 'sk', 'en'],
        imagePath: '/icon.svg',
        jsonLd,
      },
      siteOrigin,
    );
  }, [i18n.language, location.pathname, t]);

  return null;
}